/* ============================================================
   DBR HIGH PERFORMANCE — SLIPSTREAM motion engine
   dbr-slipstream.js · Project 215 · RX-5 DBR-SLIPSTREAM-2026-08
   Fail-open by design: without this file, everything is visible.
   Kit-compliant: opacity + translateY reveals only. Zero blur.
   ============================================================ */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var DESIGN = !!(window.Shopify && window.Shopify.designMode);
  var doc = document;

  /* Layer (a): hiding only exists once JS proves it is running. */
  if (!REDUCED) doc.documentElement.classList.add('dbr-motion');

  /* ---------- REVEALS ---------- */
  var SELECTOR = [
    '.dbr-section .dbr-head > *',
    '.dbr-section .dbr-card',
    '.dbr-section .dbr-gallery__item',
    '.dbr-section .dbr-stat',
    '.dbr-section .dbr-split__body > *',
    '.dbr-section .dbr-split__media',
    '.dbr-section .dbr-phero__content > *',
    '.dbr-section .dbr-band__inner > *',
    '.dbr-section .dbr-quoteband > *',
    '.dbr-section .dbr-meter > *',
    '.dbr-section .dbr-footsteps > *',
    '.dbr-partner__inner > *',
    '.dbr-cta__inner > *',
    '.dbr-marquee__heading',
    '.dbr-service-card',
    '.dbr-eco__card',
    '.dbr-eco__node',
    '.dbr-ig__rail',
    '.dbr-ig__feed',
    '.dbr-ig__caption',
    '.dbr-quote__container > *',
    '.dbr-slide-cap > *',
    '.dbr-tags .dbr-tag',
    '.dbr-benefits > li'
  ].join(',');

  var io = null;

  function collect(root) {
    var raw = Array.prototype.slice.call((root || doc).querySelectorAll(SELECTOR));
    /* namespace filter + excluded subtrees + ancestor dedupe */
    var kept = [];
    raw.forEach(function (el) {
      if (el.closest('.dbr-announce, .dbr-meganav, .dbr-footnav, header, [data-dbr-no-reveal]')) return;
      if (kept.some(function (k) { return k.contains(el); })) return;
      kept.push(el);
    });
    return kept;
  }

  function stagger(els) {
    var byParent = new Map();
    els.forEach(function (el) {
      var p = el.parentElement || doc.body;
      var i = byParent.get(p) || 0;
      el.style.setProperty('--dbr-i', Math.min(i, 7));
      byParent.set(p, i + 1);
    });
  }

  function revealNow(el) { el.classList.add('dbr-reveal', 'dbr-in'); }

  function initReveals(root) {
    if (REDUCED) return;
    var els = collect(root);
    stagger(els);
    if (!('IntersectionObserver' in window)) {
      els.forEach(revealNow);
      return;
    }
    if (!io) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { e.target.classList.add('dbr-in'); io.unobserve(e.target); }
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
    }
    els.forEach(function (el) {
      if (el.classList.contains('dbr-in')) return;
      var r = el.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) {
        /* Already on screen at init: reveal immediately, no pop-in. */
        el.classList.add('dbr-reveal');
        requestAnimationFrame(function () { el.classList.add('dbr-in'); });
      } else {
        el.classList.add('dbr-reveal');
        io.observe(el);
      }
    });
  }

  /* Layer (d): failsafe sweep — nothing can ever stay hidden. */
  setTimeout(function () {
    doc.querySelectorAll('.dbr-reveal:not(.dbr-in)').forEach(revealNow);
    doc.documentElement.classList.add('dbr-allin');
  }, 4000);

  /* ---------- COUNT-UP (Plex Mono numerals, §8.19 engine) ---------- */
  function runCount(el) {
    var raw = String(el.getAttribute('data-dbr-count') || el.textContent).trim();
    var m = raw.match(/-?[\d.,]+/);
    if (!m) return;
    var numStr = m[0].replace(/,/g, '');
    var target = parseFloat(numStr);
    if (isNaN(target)) return;
    var decimals = (numStr.split('.')[1] || '').length;
    var prefix = raw.slice(0, m.index);
    var suffix = raw.slice(m.index + m[0].length);
    if (REDUCED) { el.textContent = prefix + target.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + suffix; return; }
    var t0 = null, DUR = 1600;
    function tick(ts) {
      if (!t0) t0 = ts;
      var p = Math.min((ts - t0) / DUR, 1);
      var eased = 1 - Math.pow(1 - p, 3);
      var v = target * eased;
      if (p < 1) {
        el.textContent = prefix + v.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + suffix;
        requestAnimationFrame(tick);
      } else {
        el.textContent = prefix + target.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
        if (suffix) {
          var sf = doc.createElement('span');
          sf.className = 'dbr-suffix';
          sf.textContent = suffix;
          el.appendChild(sf);
          requestAnimationFrame(function () { sf.classList.add('dbr-pop'); });
        }
      }
    }
    requestAnimationFrame(tick);
  }

  function initCounts(root) {
    var els = (root || doc).querySelectorAll('[data-dbr-count]:not([data-dbr-counted])');
    if (!els.length) return;
    if (REDUCED || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.setAttribute('data-dbr-counted', '1'); runCount(el); });
      return;
    }
    var cio = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.setAttribute('data-dbr-counted', '1');
          runCount(e.target);
          cio.unobserve(e.target);
        }
      });
    }, { threshold: 0.35 });
    els.forEach(function (el) { cio.observe(el); });
  }

  /* ---------- METER FILL ---------- */
  function initMeters(root) {
    var els = (root || doc).querySelectorAll('[data-dbr-meter]:not([data-dbr-armed])');
    els.forEach(function (el) {
      el.setAttribute('data-dbr-armed', '1');
      var pct = Math.max(0, Math.min(1, parseFloat(el.getAttribute('data-dbr-meter')) || 0));
      function fire() { el.style.setProperty('--dbr-progressline', pct); }
      if (REDUCED || DESIGN || !('IntersectionObserver' in window)) { fire(); return; }
      var mio = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { if (e.isIntersecting) { fire(); mio.unobserve(e.target); } });
      }, { threshold: 0.35 });
      mio.observe(el);
    });
  }

  /* ---------- ROLL-UP BUTTON HOVER (§8.19) ---------- */
  function initRollups(root) {
    (root || doc).querySelectorAll('.dbr-btn:not([data-dbr-rolled]), .dbr-cta__button:not([data-dbr-rolled]), .dbr-service-card__button:not([data-dbr-rolled]), .dbr-eco__card-button:not([data-dbr-rolled])').forEach(function (btn) {
      btn.setAttribute('data-dbr-rolled', '1');
      if (REDUCED) return;
      if (btn.children.length) return; /* skip buttons with element children */
      var txt = btn.textContent.trim();
      if (!txt) return;
      btn.textContent = '';
      var wrap = doc.createElement('span');
      wrap.className = 'dbr-rollwrap';
      var span = doc.createElement('span');
      span.className = 'dbr-roll';
      span.setAttribute('data-txt', txt);
      span.textContent = txt;
      wrap.appendChild(span);
      btn.appendChild(wrap);
    });
  }

  /* ---------- WORD CYCLER (server renders spans; first visible) ---------- */
  function initCyclers(root) {
    (root || doc).querySelectorAll('.dbr-cycle:not([data-dbr-cycling])').forEach(function (el) {
      el.setAttribute('data-dbr-cycling', '1');
      var words = el.querySelectorAll('span');
      if (words.length < 2 || REDUCED) return;
      var i = 0;
      setInterval(function () {
        var next = (i + 1) % words.length;
        words[i].setAttribute('aria-hidden', 'true');
        void el.offsetWidth;
        words[next].removeAttribute('aria-hidden');
        i = next;
      }, 2600);
    });
  }

  /* ---------- PARALLAX DRIFT (Tier 2, kit amendment §0-2) ---------- */
  var driftEls = [];
  var ticking = false;
  function drift() {
    ticking = false;
    var vh = window.innerHeight;
    driftEls.forEach(function (item) {
      var r = item.el.getBoundingClientRect();
      if (r.bottom < -200 || r.top > vh + 200) return;
      var center = r.top + r.height / 2 - vh / 2;
      item.el.style.transform = 'translateY(' + (-center * item.f).toFixed(1) + 'px)';
    });
    zoomEls.forEach(function (el) {
      var r = el.getBoundingClientRect();
      if (r.bottom < -100 || r.top > vh + 100) return;
      var p = 1 - Math.min(Math.abs(r.top + r.height / 2 - vh / 2) / (vh * 0.85), 1);
      el.style.transform = 'scale(' + (0.965 + 0.035 * p).toFixed(4) + ')';
    });
  }
  function onScroll() {
    if (!ticking) { ticking = true; requestAnimationFrame(drift); }
  }
  var zoomEls = [];
  function initDrift(root) {
    if (REDUCED || DESIGN) return;
    (root || doc).querySelectorAll('[data-dbr-drift]:not([data-dbr-drifting])').forEach(function (el) {
      el.setAttribute('data-dbr-drifting', '1');
      driftEls.push({ el: el, f: parseFloat(el.getAttribute('data-dbr-drift')) || 0.1 });
    });
    (root || doc).querySelectorAll('.dbr-gallery__item, .dbr-card__media, .dbr-service-card__media, .dbr-eco__card-media').forEach(function (el) {
      if (el.getAttribute('data-dbr-zoomed') || el.hasAttribute('data-dbr-drift')) return;
      el.setAttribute('data-dbr-zoomed', '1');
      zoomEls.push(el);
    });
    if (driftEls.length && !initDrift.bound) {
      initDrift.bound = true;
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      onScroll();
    }
  }

  /* ---------- MEGA NAV (mobile toggles, esc to close) ---------- */
  function initNav(root) {
    (root || doc).querySelectorAll('.dbr-meganav:not([data-dbr-nav])').forEach(function (nav) {
      nav.setAttribute('data-dbr-nav', '1');
      var toggle = nav.querySelector('.dbr-meganav__toggle');
      if (toggle) {
        toggle.addEventListener('click', function () {
          var open = nav.classList.toggle('is-open');
          toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
      }
      nav.querySelectorAll('.dbr-meganav__item > .dbr-meganav__link[data-dbr-sub]').forEach(function (link) {
        link.addEventListener('click', function (e) {
          if (window.matchMedia('(max-width: 989px)').matches) {
            e.preventDefault();
            var li = link.parentElement;
            var open = li.classList.toggle('is-open');
            link.setAttribute('aria-expanded', open ? 'true' : 'false');
          }
        });
      });
      nav.addEventListener('keyup', function (e) {
        if (e.key === 'Escape') nav.querySelectorAll('.is-open').forEach(function (x) { x.classList.remove('is-open'); });
      });
    });
  }


  /* ---------- SPLIT-TEXT LETTERING (per-word rise, Wizard grammar) ---------- */
  var SPLIT_SEL = '.dbr-h1, .dbr-head h1, .dbr-head h2, .dbr-band .dbr-h2, .dbr-cta__heading, .dbr-marquee__heading, .dbr-quote__text, .dbr-quoteband blockquote';
  function splitWords(el) {
    return; /* v3.5: per-word splitting retired - gradient-clipped composited text
       produced cut glyphs, ghost double-renders, and invisible headings across
       engines. Heading lettering now ships as a single-element clip wipe (CSS). */
    if (el.getAttribute('data-dbr-split')) return;
    el.setAttribute('data-dbr-split', '1');
    if (REDUCED) return;
    if (el.querySelector('a, .dbr-cycle, img, svg')) return;
    var text = (el.textContent || '').trim();
    if (!text || text.split(/\s+/).length > 14) return; /* long copy reveals whole */
    var walker = doc.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    var nodes = [];
    while (walker.nextNode()) { if (walker.currentNode.nodeValue.trim()) nodes.push(walker.currentNode); }
    var i = 0;
    nodes.forEach(function (tn) {
      var frag = doc.createDocumentFragment();
      tn.nodeValue.split(/(\s+)/).forEach(function (part) {
        if (!part) return;
        if (!part.trim()) { frag.appendChild(doc.createTextNode(part)); return; }
        var w = doc.createElement('span'); w.className = 'dbr-w';
        var wi = doc.createElement('span'); wi.className = 'dbr-wi';
        wi.style.setProperty('--wd', (Math.min(i, 16) * 0.05).toFixed(2) + 's'); i++;
        wi.textContent = part;
        w.appendChild(wi); frag.appendChild(w);
      });
      tn.parentNode.replaceChild(frag, tn);
    });
  }
  function initSplits(root) {
    (root || doc).querySelectorAll(SPLIT_SEL).forEach(splitWords);
  }

  /* ---------- INIT + EDITOR SAFETY (layer c) ---------- */
  function initAll(root) {
    initSplits(root);
    initReveals(root);
    initCounts(root);
    initMeters(root);
    initRollups(root);
    initCyclers(root);
    initDrift(root);
    initNav(root);
  }

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', function () { initAll(doc); });
  } else {
    initAll(doc);
  }

  ['shopify:section:load', 'shopify:section:select', 'shopify:block:select'].forEach(function (evt) {
    doc.addEventListener(evt, function (e) {
      var root = e.target || doc;
      initAll(root);
      /* Immediately reveal anything inside a freshly re-rendered section. */
      root.querySelectorAll('.dbr-reveal:not(.dbr-in)').forEach(revealNow);
      if (root.classList && root.classList.contains('dbr-reveal')) revealNow(root);
    });
  });

  /* ---------- SCROLL PROGRESS BAR ---------- */
  if (!REDUCED) {
    var pb = doc.createElement('div');
    pb.className = 'dbr-progressbar';
    pb.setAttribute('aria-hidden', 'true');
    pb.appendChild(doc.createElement('i'));
    doc.body.appendChild(pb);
    var pTick = false;
    function pUpd() {
      pTick = false;
      var h = doc.documentElement;
      var max = h.scrollHeight - h.clientHeight;
      pb.firstChild.style.setProperty('--dbr-p', max > 0 ? (h.scrollTop / max).toFixed(4) : 0);
    }
    window.addEventListener('scroll', function () { if (!pTick) { pTick = true; requestAnimationFrame(pUpd); } }, { passive: true });
    pUpd();
  }
})();
